import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useParams, useNavigate } from "react-router-dom";
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  Replay10,
  Forward10,
  PictureInPictureAlt,
  ThumbUp,
  ThumbDown,
  Visibility
} from "@mui/icons-material";
import "./Video.scss";
import { useAuth } from "../context/AuthContext";
import {
  fetchContent,
  fetchEpisode,
  saveProgress,
  getProgress,
  getProgressCount,
  saveReaction
} from '../services/apiService';
import LikeDislike from "./LikeDislike";

export default function VideoPlayer() {
  const { id } = useParams();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [sourceUrl, setSourceUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [conteur, setConteur] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [content, setContent] = useState(null);
  const [levels, setLevels] = useState([]);           // qualités dispo par hls.js
  const [selectedLevel, setSelectedLevel] = useState(-1); 
  const [playbackRate, setPlaybackRate] = useState(1);
  const hasRegisteredView = useRef(false);
  const updateInterval = useRef(null);
  const { user } = useAuth();

  const navigate = useNavigate();
  const base = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

   useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const data = await fetchContent(id); 
        const data_count = await getProgressCount(id); // ou fetchEpisode si id épisode
        if (!mounted) return;
        //console.log("mes data api : ", data.hls_path);
        setContent(data);
        if(data_count !== null) setConteur(data_count.length);
        else setConteur(0);

        // choisir la source HLS : prefer hls_master_path sinon fallback video_path
        const hlsPath = data.hls_path || data.video_path;
        //const hlsPath = data.video_path;
        // si c'est un chemin relatif sur serveur, construis l'URL complète (API_BASE doit être concordant)
        const url = hlsPath && hlsPath.startsWith('/') ? `${base}${hlsPath}` : hlsPath;
        
        setSourceUrl(url);
      } catch (err) {
        console.error(err);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [id]);

  // init HLS when sourceUrl available

  useEffect(() => {
    const video = videoRef.current; 
    if (!video || !sourceUrl) return;

    // cleanup previous
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch(e) {}
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        // debug: true
      });
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(sourceUrl);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, (_evt, data) => {
        setLevels(data.levels || []);
        // resume position from server/local
        (async () => {
          const prog = await getProgress(id, user?.userId || null);
          //console.log("progresion... : ",prog);
          if (prog.data && prog.data.position_seconds && prog.data.position_seconds > 60) { // resume >5s
            video.currentTime = prog.data.position_seconds;
          }
        })();
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (_evt, data) => {
        // update selected level index
        setSelectedLevel(hls.currentLevel);
      });

      hls.on(Hls.Events.ERROR, (evt, data) => {
        console.error('HLS error', evt, data);
      });

      hls.on(Hls.Events.FRAG_PARSING_METADATA, () => {});
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari natif
      video.src = sourceUrl;
    } else {
      console.error('HLS non supporté');
    }

    // cleanup on unmount
    return () => {
      if (hlsRef.current) { try { hlsRef.current.destroy(); } catch(e) {} hlsRef.current = null; }
    };
  }, [sourceUrl, id]);

/*
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceUrl) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(sourceUrl);
      hls.attachMedia(video);
      hlsRef.current = hls;
    } else {
      video.src = sourceUrl;
    }
  }, [sourceUrl]);
*/

/*
  const togglePlay = () => {
    const v = videoRef.current;
    if (v.paused) v.play();
    else v.pause();
  };
*/

//console.log("user id : ", user);
/*
const togglePlay = async () => {
  const v = videoRef.current;
  if (!v) return;

  if (v.paused) {
    await v.play();

    // ✅ Si la vidéo vient d’être jouée pour la première fois :
    if (!hasRegisteredView.current) {
      //const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      setTimeout(async () => {

        const positionSeconds = Math.floor(v.currentTime || 0);
        const durationSeconds = Math.floor(v.duration || 0);
        
        
        console.log("durationSeconds : ", positionSeconds);

        // ✅ Appel avec infos complètes
        await saveProgress({
          contentId: id,
          userId: user?.userId || null,
          positionSeconds,
          durationSeconds,
        });

        hasRegisteredView.current = true;

      }, 10000);
    }

  } else {
    v.pause();
  }
};
*/
const togglePlay = async () => {
  const v = videoRef.current;
  if (!v) return;

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  if (v.paused) {
    await v.play();

    // ✅ Première lecture → on enregistre une première vue
    if (!hasRegisteredView.current) {
      setTimeout(async () => {
        const positionSeconds = Math.floor(v.currentTime || 0);
        const durationSeconds = Math.floor(v.duration || 0);

        await saveProgress({
          contentId: id,
          userId: user?.userId || null,
          positionSeconds,
          durationSeconds,
        });

        hasRegisteredView.current = true;
      }, 5000); // après 5s de lecture
    }

    // ✅ Démarre la mise à jour automatique toutes les 30 secondes
    if (!updateInterval.current) {
      updateInterval.current = setInterval(async () => {
        if (!v.paused && !v.ended) {
          const positionSeconds = Math.floor(v.currentTime || 0);
          const durationSeconds = Math.floor(v.duration || 0);

          //console.log("🕒 Auto-save progress:", positionSeconds);

          await saveProgress({
            contentId: id,
            userId: user?.userId || null,
            positionSeconds,
            durationSeconds,
          });
        }
      }, 30000); // 30 secondes
    }

  } else {
    v.pause();
    // ✅ Stoppe la mise à jour auto quand on met en pause
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
      updateInterval.current = null;
    }
  }
};

// ✅ Nettoyage automatique quand on quitte la page
useEffect(() => {
  return () => {
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
    }
  };
}, []);



  const toggleMute = () => {
    const v = videoRef.current;
    v.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleProgress = () => {
    const v = videoRef.current;
    setPosition(v.currentTime);
    setProgress((v.currentTime / v.duration) * 100);
    setDuration(v.duration);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    const newTime = (e.target.value / 100) * duration;
    v.currentTime = newTime;
    setProgress(e.target.value);
  };
  

  function changePlaybackRate(rate) {
    const v = videoRef.current;
    v.playbackRate = rate;
    setPlaybackRate(rate);
  }
  
  // quality selector
  function onQualitySelect(e) {
    const idx = parseInt(e.target.value, 10);
    if (!hlsRef.current) return;
    if (idx === -1) {
      hlsRef.current.currentLevel = -1; // auto
    } else {
      hlsRef.current.currentLevel = idx;
    }
    setSelectedLevel(idx);
  }

  const toggleFullscreen = () => {
    const container = videoRef.current.parentElement;
    if (!document.fullscreenElement) container.requestFullscreen();
    else document.exitFullscreen();
    setIsFullscreen(!isFullscreen);
  };

  const formatTime = (sec) => {
    if (isNaN(sec)) return "00:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };
  
function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}


  return (
    <>
    
    <div className="page-content">
      <div
        className="video-wrapper"
        onMouseMove={() => {
          setShowControls(true);
          clearTimeout(window.hideControlsTimeout);
          window.hideControlsTimeout = setTimeout(
            () => setShowControls(false),
            2500
          );
        }}
      >
        <video
          ref={videoRef}
          onTimeUpdate={handleProgress}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          className="video-player"
          poster={`${base}/api/${content?.image|| null}`}
        />

        {/* Boutons centraux (sur hover) */}
        {showControls && (
          <div className="center-controls">
            <button
              className="left-skip"
              onClick={() => (videoRef.current.currentTime -= 10)}
            >
              <Replay10 />
            </button>

            <button className="main-play" onClick={togglePlay}>
              {isPlaying ? <Pause /> : <PlayArrow />}
            </button>

            <button
              className="right-skip"
              onClick={() => (videoRef.current.currentTime += 10)}
            >
              <Forward10 />
            </button>
          </div>
        )}

        {/* Barre de contrôle bas */}
        {showControls && (
          <div className="controls-overlay">
            <input
              type="range"
              className="progress-bar"
              min="0"
              max="100"
              value={progress}
              onChange={handleSeek}
            />

            <div className="controls-bar">
              <div className="controls-left">
                <button onClick={togglePlay}>
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </button>

                <button onClick={toggleMute}>
                  {isMuted ? <VolumeOff /> : <VolumeUp />}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setVolume(v);
                    videoRef.current.volume = v;
                    setIsMuted(v === 0);
                  }}
                  className="volume-bar"
                />

                <span className="time">
                  {formatTime(position)} / {formatTime(duration)}
                </span>
              </div>

              <div className="controls-right">
                
                <select value={playbackRate} onChange={(e)=>changePlaybackRate(Number(e.target.value))} className="playbackRate">
                  <option value={0.5}>0.5x</option>
                  <option value={0.75}>0.75x</option>
                  <option value={1}>1x</option>
                  <option value={1.25}>1.25x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>

                <select value={selectedLevel} onChange={onQualitySelect} title="Qualité"  className="selectedLevel">
                  <option value={-1}>Auto</option>
                  {levels.map((lvl, i) => (
                    <option key={i} value={i}>{(lvl.height || 'auto')}p - {Math.round((lvl.bitrate||0)/1000)} kbps</option>
                  ))}
                </select>
                
                <button onClick={() => videoRef.current.requestPictureInPicture()}>
                  <PictureInPictureAlt />
                </button>

                <button onClick={toggleFullscreen}>
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="video-info-bar">
        <div className="info-left">
          <h2>{content?.title}</h2>
          <p  className="info-left-date">Publié le {formatDate(content?.created_at)}</p> 
        </div>

        <div className="info-right">
          <span className="views">
            <Visibility size={16} /> {conteur} vues
          </span>
          <LikeDislike contentId={id} userParamId={user?.userId} />
        </div>
        
      </div>

      
      <div className="video-info-bar-sup">
        <p>{content?.description}</p> 
      </div>

    </div>
    </>
    
  );
}
